<template>
    <div ref="mapWrapper" class="map-wrapper">
        <l-map
            ref="leafletMap"
            :zoom="mapConfig.zoom"
            :center="centerArray"
            :min-zoom="mapConfig.minZoom"
            :max-zoom="mapConfig.maxZoom"
            :options="mapOptions"
            style="height: 100%; width: 100%;"
            @ready="onMapReady"
        >
            <l-tile-layer
                v-if="mapConfig.mapTypeId === 'roadmap'"
                :url="tileLayerUrl"
                :attribution="attribution"
            />

            <l-tile-layer
                v-else
                :url="satelliteTileLayerUrl"
                :attribution="satelliteAttribution"
            />

            <template v-if="isMapReady">
                <slot
                    :map="map"
                    :bounds="bounds"
                    :project="project"
                />
            </template>
        </l-map>

        <!-- Barra de colores LAeq -->
        <div class="laeq-legend">
            <div class="legend-title">LAeq (dBA)</div>
            <div
                v-for="range in colorRanges"
                :key="range.label"
                class="legend-item"
            >
                <div
                    class="legend-color"
                    :style="{ backgroundColor: range.color }"
                ></div>
                <div class="legend-label">{{ range.label }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { LMap, LTileLayer } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

export default {
    components: {
        LMap,
        LTileLayer
    },

    props: {
        mapConfig: {
            type: Object,
            required: true
        },
        heatMap: {
            type: String,
            default: ''
        },
        project: {
            type: String,
            default: ''
        },
        active: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            map: null,
            bounds: null,
            isMapReady: false,
            markersCount: 0,
            // OpenStreetMap (gratuito)
            tileLayerUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            // Satellite (ESRI - gratuito)
            satelliteTileLayerUrl: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            satelliteAttribution: '&copy; <a href="https://www.esri.com/">Esri</a>',
            mapOptions: {
                zoomControl: true,
                attributionControl: true
            },
            colorRanges: [
                { label: '≥ 80', color: '#430A4A' },
                { label: '75-80', color: '#75085C' },
                { label: '70-75', color: '#A11A4D' },
                { label: '65-70', color: '#CD463E' },
                { label: '60-65', color: '#E87E4D' },
                { label: '55-60', color: '#F3C683' },
                { label: '50-55', color: '#E2F2BF' },
                { label: '45-50', color: '#CEE4CC' },
                { label: '40-45', color: '#B8D6D1' },
                { label: '< 40', color: '#A0BABF' }
            ],
            resizeObserver: null,
            invalidateRaf: null,
            delayedRefreshTimer: null,
            onWindowResize: null
        }
    },

    computed: {
        centerArray() {
            if (this.mapConfig && this.mapConfig.center) {
                return [this.mapConfig.center.lat, this.mapConfig.center.lng]
            }
            return [0, 0]
        }
    },

    methods: {
        onMapReady() {
            this.$nextTick(() => {
                this.map = this.$refs.leafletMap.mapObject
                this.bounds = L.latLngBounds([])
                this.isMapReady = true
                this.scheduleMapRefresh({ fitBounds: true })
                this.scheduleMapRefresh({ fitBounds: true, delayMs: 350 })
            })
        },

        addMarkerToBounds(latLng) {
            if (this.bounds) {
                this.bounds.extend(latLng)
                this.markersCount++
            }
        },
       
        fitBounds() {
            if (this.map && this.bounds && this.bounds.isValid()) {
                const configPadding = this.mapConfig && Array.isArray(this.mapConfig.fitBoundsPadding)
                    ? this.mapConfig.fitBoundsPadding
                    : null
                const padding = configPadding && configPadding.length === 2
                    ? configPadding
                    : [50, 50]

                const fitBoundsOptions = {
                    padding,
                    animate: false
                }

                const rawMaxZoom = Number(this.mapConfig && this.mapConfig.fitBoundsMaxZoom)
                if (Number.isFinite(rawMaxZoom)) {
                    fitBoundsOptions.maxZoom = rawMaxZoom
                }

                this.map.fitBounds(this.bounds, fitBoundsOptions)
            }
        },

        scheduleMapRefresh({ fitBounds = false, delayMs = 0 } = {}) {
            if (delayMs > 0) {
                clearTimeout(this.delayedRefreshTimer)
                this.delayedRefreshTimer = setTimeout(() => {
                    this.scheduleMapRefresh({ fitBounds })
                }, delayMs)
                return
            }

            if (!this.map) return

            if (this.invalidateRaf) {
                cancelAnimationFrame(this.invalidateRaf)
            }

            this.invalidateRaf = requestAnimationFrame(() => {
                this.map.invalidateSize({ pan: false, animate: false })
                if (fitBounds) this.fitBounds()
                this.invalidateRaf = null
            })
        },

        setupResizeObserver() {
            if (typeof ResizeObserver === 'undefined' || !this.$refs.mapWrapper) return
            this.resizeObserver = new ResizeObserver(() => {
                this.scheduleMapRefresh()
                this.scheduleMapRefresh({ delayMs: 280 })
            })
            this.resizeObserver.observe(this.$refs.mapWrapper)
        },

        setupWindowListeners() {
            this.onWindowResize = () => {
                this.scheduleMapRefresh()
                this.scheduleMapRefresh({ delayMs: 180 })
            }
            window.addEventListener('resize', this.onWindowResize, { passive: true })
            window.addEventListener('orientationchange', this.onWindowResize, { passive: true })
        },

        teardownLayoutListeners() {
            if (this.onWindowResize) {
                window.removeEventListener('resize', this.onWindowResize)
                window.removeEventListener('orientationchange', this.onWindowResize)
                this.onWindowResize = null
            }

            if (this.resizeObserver) {
                this.resizeObserver.disconnect()
                this.resizeObserver = null
            }

            if (this.invalidateRaf) {
                cancelAnimationFrame(this.invalidateRaf)
                this.invalidateRaf = null
            }

            if (this.delayedRefreshTimer) {
                clearTimeout(this.delayedRefreshTimer)
                this.delayedRefreshTimer = null
            }
        }
    },

    watch: {
        project() {
            if (this.map) {
                this.bounds = L.latLngBounds([])
                this.scheduleMapRefresh({ fitBounds: true })
                this.scheduleMapRefresh({ fitBounds: true, delayMs: 350 })
            }
        },

        centerArray() {
            this.scheduleMapRefresh()
        },

        active(newVal) {
            if (newVal) {
                this.scheduleMapRefresh({ fitBounds: true })
                this.scheduleMapRefresh({ fitBounds: true, delayMs: 350 })
            }
        }
    },

    mounted() {
        this.setupWindowListeners()
        this.$nextTick(() => {
            this.setupResizeObserver()
        })
    },

    beforeDestroy() {
        this.teardownLayoutListeners()
    }
}
</script>

<style scoped>
.map-wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;
}

.leaflet-container {
    height: 100%;
    width: 100%;
}

/* Leyenda de colores LAeq */
.laeq-legend {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.9);
    padding: 12px 10px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    z-index: 1000;
    font-family: Arial, sans-serif;
    min-width: 100px;
    pointer-events: none;
}

.legend-title {
    font-weight: bold;
    font-size: 13px;
    margin-bottom: 8px;
    text-align: center;
    color: #333;
    border-bottom: 1px solid #ddd;
    padding-bottom: 5px;
}

.legend-item {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}

.legend-item:last-child {
    margin-bottom: 0;
}

.legend-color {
    width: 25px;
    height: 18px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    margin-right: 8px;
    flex-shrink: 0;
}

.legend-label {
    font-size: 11px;
    color: #555;
    white-space: nowrap;
}

@media (max-width: 960px) {
    .laeq-legend {
        right: 8px;
        top: auto;
        bottom: 12px;
        transform: none;
        padding: 8px 8px;
        min-width: 88px;
        max-height: 48vh;
        overflow-y: auto;
    }

    .legend-title {
        font-size: 11px;
        margin-bottom: 6px;
    }

    .legend-color {
        width: 20px;
        height: 14px;
        margin-right: 6px;
    }

    .legend-label {
        font-size: 10px;
    }
}

@media (max-width: 600px) {
    .laeq-legend {
        right: 6px;
        bottom: 8px;
        padding: 6px;
        border-radius: 6px;
        min-width: 76px;
    }

    .legend-title {
        font-size: 10px;
    }

    .legend-item {
        margin-bottom: 3px;
    }

    .legend-color {
        width: 16px;
        height: 12px;
    }

    .legend-label {
        font-size: 9px;
    }
}
</style>
